import React, { useState, useCallback, useEffect, useRef, memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { STRUCTURED_COMPONENTS } from './index.js';
import { useAppTheme } from '../../contexts/ThemeContext';

const getStyles = (theme) => ({
  container: {
    position: 'relative',
  },
  textContent: {
    color: theme.colors.text.primary,
    fontSize: '15px',
    lineHeight: '1',
    whiteSpace: 'pre-wrap',
    marginBottom: theme.spacing.md,
  },
  structuredContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.md,
  },
  mixedContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.md,
  },
  errorComponent: {
    backgroundColor: theme.colors.error.background,
    color: theme.colors.error.text,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.error.border}`,
    fontSize: '14px',
  },
  emptyState: {
    color: theme.colors.text.muted,
    fontStyle: 'italic',
    textAlign: 'center',
    padding: theme.spacing.lg,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.colors.overlayDark,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.text.primary,
    fontSize: '14px',
    borderRadius: theme.borderRadius.md,
  },
  errorMessage: {
    backgroundColor: theme.colors.error.background,
    color: theme.colors.error.text,
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
    border: `1px solid ${theme.colors.error.border}`,
    fontSize: '12px',
    marginTop: theme.spacing.sm,
  },
  markdownHeading: {
    margin: '16px 0 8px 0',
    fontWeight: '600',
    color: theme.colors.text.primary,
  },
  markdownParagraph: {
    margin: '8px 0',
    color: theme.colors.text.primary,
  },
  markdownList: {
    margin: '8px 0',
    paddingLeft: '20px',
    color: theme.colors.text.primary,
  },
  markdownListItem: {
    margin: '4px 0',
  },
  markdownCode: {
    backgroundColor: theme.colors.code.background,
    padding: '2px 4px',
    borderRadius: '3px',
    fontSize: '13px',
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    color: theme.colors.text.primary,
    border: `1px solid ${theme.colors.code.border}`,
  },
  markdownPre: {
    backgroundColor: theme.colors.code.background,
    padding: '12px',
    borderRadius: '6px',
    overflow: 'auto',
    margin: '8px 0',
    border: `1px solid ${theme.colors.code.border}`,
    fontSize: '13px',
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    maxWidth: '800px',
  },
  markdownBlockquote: {
    borderLeft: `4px solid ${theme.colors.text.muted}`,
    paddingLeft: '16px',
    margin: '8px 0',
    fontStyle: 'italic',
    color: theme.colors.text.secondary,
  },
  markdownStrong: {
    fontWeight: '700',
    color: theme.colors.text.primary,
  },
  markdownEm: {
    fontStyle: 'italic',
    color: theme.colors.text.primary,
  },
  markdownTableWrapper: {
    overflowX: 'auto',
    margin: '8px 0',
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.border}`,
  },
  markdownTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  markdownThead: {
    backgroundColor: theme.colors.background.modal,
  },
  markdownTr: {
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  markdownTh: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    textAlign: 'left',
    fontWeight: '600',
    fontSize: '12px',
    textTransform: 'uppercase',
    color: theme.colors.text.secondary,
    whiteSpace: 'nowrap',
  },
  markdownTd: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    color: theme.colors.text.primary,
    verticalAlign: 'top',
  },
});

/**
 * Main renderer for structured responses from AI agents
 * Handles both text and structured component rendering
 */
const StructuredRenderer = ({ response, onFormSubmit, authToken }) => {
  const { theme } = useAppTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastValidContent, setLastValidContent] = useState(null);
  const lastResponseRef = useRef(null);
  const hasLoggedRef = useRef(false);

  // Only log when response actually changes (using deep comparison for objects)
  // Use ref to track if we've already logged this exact response
  useEffect(() => {
    let responseKey;
    if (typeof response === 'string') {
      responseKey = response;
    } else if (response && typeof response === 'object') {
      // For objects, use a stable string representation
      try {
        responseKey = JSON.stringify(response);
      } catch (e) {
        responseKey = String(response);
      }
    } else {
      responseKey = String(response);
    }

    // Only log if the response actually changed AND we haven't logged this exact response before
    if (lastResponseRef.current !== responseKey) {
      lastResponseRef.current = responseKey;
      hasLoggedRef.current = false; // Reset log flag for new response
    }

    // Only log once per unique response, and only for significant content
    if (!hasLoggedRef.current && response && typeof response === 'string' && response.length > 10) {
      hasLoggedRef.current = true;
      console.log(
        'StructuredRenderer: Response updated:',
        response.substring(0, 100) + (response.length > 100 ? '...' : ''),
      );
    }
  }, [response]);

  const handleFormSubmit = useCallback(
    async (formId, formData) => {
      if (!onFormSubmit) {
        console.warn('No form submit handler provided');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        await onFormSubmit(formId, formData);
      } catch (err) {
        console.error('Form submission error:', err);
        setError(err.message || 'Failed to submit form');
      } finally {
        setLoading(false);
      }
    },
    [onFormSubmit],
  );

  const renderComponent = (component, index) => {
    const { type, props, id } = component;
    const Component = STRUCTURED_COMPONENTS[type];
    const componentKey = id || index;

    if (!Component) {
      console.warn(`Unknown component type: ${type}`);
      return (
        <div key={componentKey} style={styles.errorComponent}>
          <p>Unknown component type: {type}</p>
        </div>
      );
    }

    // Extract key from props to avoid React warning
    const componentProps = {
      ...props,
      onFormSubmit: type === 'form' ? handleFormSubmit : undefined,
      authToken: authToken,
    };

    try {
      return <Component key={componentKey} {...componentProps} />;
    } catch (err) {
      console.error(`Error rendering component ${type}:`, err);
      return (
        <div key={componentKey} style={styles.errorComponent}>
          <p>
            Error rendering {type} component: {err.message}
          </p>
        </div>
      );
    }
  };

  // Update last valid content when response changes (only for string responses)
  useEffect(() => {
    if (response && typeof response === 'string' && response.trim() !== '') {
      setLastValidContent(response);
    }
  }, [response]);

  const renderContent = () => {
    // For structured content (objects), process directly
    if (response && typeof response === 'object' && response !== null) {
      // Handle structured responses
      // Check if this is a nested content structure (from streaming)
      let actualResponse = response;
      if (response.type === 'content' && response.content && typeof response.content === 'object') {
        actualResponse = response.content;
      }

      const { type, content, components } = actualResponse;

      switch (type) {
        case 'structured':
          if (components && Array.isArray(components)) {
            return (
              <div style={styles.structuredContent}>
                {components.map((component, index) => renderComponent(component, index))}
              </div>
            );
          } else {
            console.warn('Structured response missing components array');
            return <div style={styles.errorComponent}>Invalid structured response format</div>;
          }

        case 'content':
          if (content) {
            return <div style={styles.textContent}>{content}</div>;
          } else {
            console.warn('Content response missing content field');
            return <div style={styles.errorComponent}>Invalid content response format</div>;
          }

        default:
          console.warn(`Unknown response type: ${type}`);
          return <div style={styles.textContent}>{JSON.stringify(response, null, 2)}</div>;
      }
    } else if (response && typeof response === 'string' && response.trim() !== '') {
      // For valid string content, use it directly
      return (
        <div style={styles.textContent}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => <h1 style={styles.markdownHeading}>{children}</h1>,
              h2: ({ children }) => <h2 style={styles.markdownHeading}>{children}</h2>,
              h3: ({ children }) => <h3 style={styles.markdownHeading}>{children}</h3>,
              h4: ({ children }) => <h4 style={styles.markdownHeading}>{children}</h4>,
              h5: ({ children }) => <h5 style={styles.markdownHeading}>{children}</h5>,
              h6: ({ children }) => <h6 style={styles.markdownHeading}>{children}</h6>,
              p: ({ children }) => <p style={styles.markdownParagraph}>{children}</p>,
              ul: ({ children }) => <ul style={styles.markdownList}>{children}</ul>,
              ol: ({ children }) => <ol style={styles.markdownList}>{children}</ol>,
              li: ({ children }) => <li style={styles.markdownListItem}>{children}</li>,
              code: ({ node, inline, className, children, ...props }) => {
                const match = /language-(\w+)/.exec(node?.properties?.className || '');

                if (!match) {
                  return (
                    <code {...props} style={styles.markdownCode}>
                      {children}
                    </code>
                  );
                } else {
                  return (
                    <pre style={styles.markdownPre}>
                      <code {...props} style={styles.markdownCode}>
                        {children}
                      </code>
                    </pre>
                  );
                }
              },
              blockquote: ({ children }) => <blockquote style={styles.markdownBlockquote}>{children}</blockquote>,
              strong: ({ children }) => <strong style={styles.markdownStrong}>{children}</strong>,
              em: ({ children }) => <em style={styles.markdownEm}>{children}</em>,
              table: ({ children }) => (
                <div style={styles.markdownTableWrapper}>
                  <table style={styles.markdownTable}>{children}</table>
                </div>
              ),
              thead: ({ children }) => <thead style={styles.markdownThead}>{children}</thead>,
              tbody: ({ children }) => <tbody>{children}</tbody>,
              tr: ({ children }) => <tr style={styles.markdownTr}>{children}</tr>,
              th: ({ children }) => <th style={styles.markdownTh}>{children}</th>,
              td: ({ children }) => <td style={styles.markdownTd}>{children}</td>,
            }}
          >
            {response}
          </ReactMarkdown>
        </div>
      );
    } else if (lastValidContent && typeof lastValidContent === 'string') {
      // For empty responses, use last valid string content
      return (
        <div style={styles.textContent}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => <h1 style={styles.markdownHeading}>{children}</h1>,
              h2: ({ children }) => <h2 style={styles.markdownHeading}>{children}</h2>,
              h3: ({ children }) => <h3 style={styles.markdownHeading}>{children}</h3>,
              h4: ({ children }) => <h4 style={styles.markdownHeading}>{children}</h4>,
              h5: ({ children }) => <h5 style={styles.markdownHeading}>{children}</h5>,
              h6: ({ children }) => <h6 style={styles.markdownHeading}>{children}</h6>,
              p: ({ children }) => <p style={styles.markdownParagraph}>{children}</p>,
              ul: ({ children }) => <ul style={styles.markdownList}>{children}</ul>,
              ol: ({ children }) => <ol style={styles.markdownList}>{children}</ol>,
              li: ({ children }) => <li style={styles.markdownListItem}>{children}</li>,
              code: ({ node, inline, className, children, ...props }) => {
                const match = /language-(\w+)/.exec(node?.properties?.className || '');

                if (!match) {
                  return (
                    <code {...props} style={styles.markdownCode}>
                      {children}
                    </code>
                  );
                } else {
                  return (
                    <pre style={styles.markdownPre}>
                      <code {...props} style={styles.markdownCode}>
                        {children}
                      </code>
                    </pre>
                  );
                }
              },
              blockquote: ({ children }) => <blockquote style={styles.markdownBlockquote}>{children}</blockquote>,
              strong: ({ children }) => <strong style={styles.markdownStrong}>{children}</strong>,
              em: ({ children }) => <em style={styles.markdownEm}>{children}</em>,
              table: ({ children }) => (
                <div style={styles.markdownTableWrapper}>
                  <table style={styles.markdownTable}>{children}</table>
                </div>
              ),
              thead: ({ children }) => <thead style={styles.markdownThead}>{children}</thead>,
              tbody: ({ children }) => <tbody>{children}</tbody>,
              tr: ({ children }) => <tr style={styles.markdownTr}>{children}</tr>,
              th: ({ children }) => <th style={styles.markdownTh}>{children}</th>,
              td: ({ children }) => <td style={styles.markdownTd}>{children}</td>,
            }}
          >
            {lastValidContent}
          </ReactMarkdown>
        </div>
      );
    } else {
      return <div style={styles.emptyState}>No content to display</div>;
    }
  };

  return (
    <div style={styles.container}>
      {renderContent()}
      {loading && <div style={styles.loadingOverlay}>Processing...</div>}
      {error && <div style={styles.errorMessage}>{error}</div>}
    </div>
  );
};

// Memoize StructuredRenderer to prevent unnecessary re-renders
// Only re-render if response, onFormSubmit, or authToken actually changes
export default memo(StructuredRenderer, (prevProps, nextProps) => {
  // Compare responses
  const prevResponse = prevProps.response;
  const nextResponse = nextProps.response;

  let responsesEqual = false;
  if (prevResponse === nextResponse) {
    responsesEqual = true;
  } else if (typeof prevResponse === 'string' && typeof nextResponse === 'string') {
    responsesEqual = prevResponse === nextResponse;
  } else if (prevResponse && nextResponse && typeof prevResponse === 'object' && typeof nextResponse === 'object') {
    try {
      responsesEqual = JSON.stringify(prevResponse) === JSON.stringify(nextResponse);
    } catch (e) {
      responsesEqual = false;
    }
  }

  return (
    responsesEqual && prevProps.onFormSubmit === nextProps.onFormSubmit && prevProps.authToken === nextProps.authToken
  );
});
